import { useEffect, useState } from 'react'
import { useStore } from '../store/AppStore'
import { usePlayer } from '../store/PlayerContext'
import {
  appProgress,
  auditionTracks,
  nextUp,
  openTasks,
  primaryApplication,
  studyStreak,
  weekStudy,
} from '../store/selectors'
import { countdown, fmtDate, fmtMins, greeting, isOverdue, num, relDay, today } from '../lib/date'
import { Button, Check, Meta, Progress, hueVars } from '../components/ui'
import { IconPause, IconPlay } from '../components/icons'
import { useT, type Translate } from '../i18n'
import type { Screen } from '../App'
import type { AppState, TileId } from '../store/types'

const TILE_LABEL_KEYS = {
  countdown: 'tile.countdown',
  nextUp: 'tile.nextUp',
  tasks: 'tile.tasks',
  studyWeek: 'tile.studyWeek',
  audition: 'tile.audition',
  quickNote: 'tile.quickNote',
  weather: 'tile.weather',
  player: 'tile.player',
} as const

export default function Today({ go }: { go: (s: Screen) => void }) {
  const { state, setSettings } = useStore()
  const { settings } = state
  const t = useT()
  const [editing, setEditing] = useState(false)

  const visible = settings.tileOrder.filter((id) => !settings.hiddenTiles.includes(id))
  const hidden = settings.tileOrder.filter((id) => settings.hiddenTiles.includes(id))

  const move = (id: TileId, dir: -1 | 1) => {
    const order = [...settings.tileOrder]
    const i = order.indexOf(id)
    const j = i + dir
    if (j < 0 || j >= order.length) return
    ;[order[i], order[j]] = [order[j], order[i]]
    setSettings({ tileOrder: order })
  }

  const resize = (id: TileId) =>
    setSettings({
      wideTiles: settings.wideTiles.includes(id)
        ? settings.wideTiles.filter((x) => x !== id)
        : [...settings.wideTiles, id],
    })

  const hide = (id: TileId) => setSettings({ hiddenTiles: [...settings.hiddenTiles, id] })
  const restore = (id: TileId) =>
    setSettings({ hiddenTiles: settings.hiddenTiles.filter((x) => x !== id) })

  return (
    <div className="stack stack--lg">
      <div className="hero">
        <Meta className="hero__eyebrow">
          {fmtDate(today(), { weekday: 'long', day: 'numeric', month: 'long' })}
        </Meta>
        <h1>
          {greeting()}
          {t('today.comma')}
          <br />
          <em>{settings.name}</em>
        </h1>
        <p className="hero__sub">{summaryLine(state, t)}</p>
      </div>

      <div
        className="row row--wrap"
        style={{ gap: 'var(--s-2)', marginTop: 'calc(-1 * var(--s-3))' }}
      >
        <Button size="sm" variant={editing ? 'primary' : 'ghost'} onClick={() => setEditing((e) => !e)}>
          {editing ? t('common.done') : t('today.customise')}
        </Button>
        {editing &&
          hidden.map((id) => (
            <button key={id} className="chip" onClick={() => restore(id)}>
              + {t(TILE_LABEL_KEYS[id])}
            </button>
          ))}
      </div>

      <div className="bento">
        {visible.map((id) => (
          <TileFrame
            key={id}
            id={id}
            wide={settings.wideTiles.includes(id)}
            editing={editing}
            onMove={move}
            onResize={resize}
            onHide={hide}
          >
            <TileBody id={id} go={go} />
          </TileFrame>
        ))}
      </div>
    </div>
  )
}

function summaryLine(state: AppState, t: Translate): string {
  const open = openTasks(state).length
  const app = primaryApplication(state)
  const bits: string[] = []
  if (app)
    bits.push(
      t('today.toInstitution', {
        time: countdown(app.deadline),
        name: app.institution.split('—')[0].trim(),
      }),
    )
  bits.push(
    open === 0
      ? t('today.nothingOpen')
      : t(open === 1 ? 'today.openTask' : 'today.openTasks', { n: open }),
  )
  const streak = studyStreak(state)
  if (streak > 1) bits.push(t('today.streak', { n: streak }))
  return bits.join(' · ')
}

/* ------------------------------------------------------------ tile chrome */

function TileFrame({
  id,
  wide,
  editing,
  onMove,
  onResize,
  onHide,
  children,
}: {
  id: TileId
  wide: boolean
  editing: boolean
  onMove: (id: TileId, dir: -1 | 1) => void
  onResize: (id: TileId) => void
  onHide: (id: TileId) => void
  children: React.ReactNode
}) {
  const t = useT()
  return (
    <div className={`tile-slot ${wide ? 'span-2' : ''}`}>
      {editing && (
        <div className="tile__tools">
          <button className="tile__tool" onClick={() => onMove(id, -1)} aria-label={t('tile.moveEarlier')}>
            <span className="icon-flip">←</span>
          </button>
          <button className="tile__tool" onClick={() => onMove(id, 1)} aria-label={t('tile.moveLater')}>
            <span className="icon-flip">→</span>
          </button>
          <button className="tile__tool" onClick={() => onResize(id)} aria-label={t('tile.resize')}>
            ⤢
          </button>
          <button
            className="tile__tool tile__tool--danger"
            onClick={() => onHide(id)}
            aria-label={t('tile.hide', { name: t(TILE_LABEL_KEYS[id]) })}
          >
            ✕
          </button>
        </div>
      )}
      <div className={`tile-slot__inner ${editing ? 'tile--editing' : ''}`}>{children}</div>
    </div>
  )
}

/* --------------------------------------------------------------- the tiles */

function TileBody({ id, go }: { id: TileId; go: (s: Screen) => void }) {
  const { state, toggleTask, setQuickNote, pinNote } = useStore()
  const t = useT()
  const arrow = t('common.arrow')

  switch (id) {
    case 'countdown': {
      const app = primaryApplication(state)
      if (!app)
        return (
          <button className="tile" onClick={() => go('journey')} style={{ flex: 1 }}>
            <Meta>{t('tile.journeyTitle')}</Meta>
            <div className="tile__foot">
              {t('tile.addFirstApplication')} {arrow}
            </div>
          </button>
        )
      return (
        <button
          className="tile tile--tinted"
          style={{ ...hueVars('iris'), flex: 1 }}
          onClick={() => go('journey')}
        >
          <div className="row">
            <Meta className="grow">{app.institution}</Meta>
            <span className={`countdown ${isOverdue(app.deadline) ? 'countdown--urgent' : ''}`}>
              {countdown(app.deadline)}
            </span>
          </div>
          <div style={{ fontSize: 'var(--t-base)', fontWeight: 600, lineHeight: 1.3 }}>
            {app.course}
          </div>
          <div style={{ marginTop: 'auto', paddingTop: 'var(--s-3)' }}>
            <Progress value={appProgress(app)} hue="iris" />
            <div className="tile__foot" style={{ marginTop: 6 }}>
              {t('tile.requirementsDone', {
                done: app.requirements.filter((r) => r.done).length,
                total: app.requirements.length,
              })}
            </div>
          </div>
        </button>
      )
    }

    case 'nextUp': {
      const e = nextUp(state)
      return (
        <button className="tile" onClick={() => go('calendar')} style={{ flex: 1 }}>
          <Meta>{t('tile.nextUpTitle')}</Meta>
          {e ? (
            <>
              <div style={{ fontSize: 'var(--t-md)', fontWeight: 600, lineHeight: 1.25 }}>
                {e.title}
              </div>
              <div className="tile__foot">
                {relDay(e.date)}
                {e.start ? ` · ${e.start}` : ''}
              </div>
            </>
          ) : (
            <div className="tile__foot" style={{ marginTop: 'var(--s-2)' }}>
              {t('tile.nothingScheduled')} {arrow}
            </div>
          )}
        </button>
      )
    }

    case 'tasks': {
      const list = openTasks(state).slice(0, 3)
      return (
        <div className="tile" style={{ flex: 1 }}>
          <div className="row">
            <Meta className="grow">{t('tile.tasksTitle')}</Meta>
            <button className="btn btn--quiet btn--sm" onClick={() => go('study')}>
              {t('tile.all')} {arrow}
            </button>
          </div>
          {list.length === 0 ? (
            <div className="tile__foot">{t('tile.allClear')}</div>
          ) : (
            <div style={{ marginTop: 'calc(-1 * var(--s-1))' }}>
              {list.map((task) => {
                const subject = state.subjects.find((s) => s.id === task.subjectId)
                return (
                  <Check
                    key={task.id}
                    checked={task.done}
                    hue={subject?.hue}
                    onChange={() => toggleTask(task.id)}
                    label={
                      <span style={{ fontSize: 'var(--t-sm)' }}>
                        {task.title}
                        {task.due && (
                          <span
                            className="countdown"
                            style={{
                              display: 'block',
                              marginTop: 2,
                              color: isOverdue(task.due) ? 'var(--danger)' : 'var(--ink-3)',
                            }}
                          >
                            {relDay(task.due)}
                          </span>
                        )}
                      </span>
                    }
                  />
                )
              })}
            </div>
          )}
        </div>
      )
    }

    case 'studyWeek': {
      const w = weekStudy(state)
      return (
        <button className="tile" onClick={() => go('study')} style={{ flex: 1 }}>
          <Meta>{t('tile.thisWeek')}</Meta>
          <div className="tile__stat">{fmtMins(w.totalMins)}</div>
          <div style={{ marginTop: 'auto', paddingTop: 'var(--s-3)' }}>
            <Progress value={w.targetMins ? (w.totalMins / w.targetMins) * 100 : 0} hue="sage" />
            <div className="tile__foot" style={{ marginTop: 6 }}>
              {t('tile.ofTarget', { x: fmtMins(w.targetMins) })}
            </div>
          </div>
        </button>
      )
    }

    case 'audition': {
      const pieces = auditionTracks(state)
      return (
        <button
          className="tile tile--tinted"
          style={{ ...hueVars('rose'), flex: 1 }}
          onClick={() => go('library')}
        >
          <Meta>{t('tile.auditionTitle')}</Meta>
          <div className="tile__stat">{num(pieces.length)}</div>
          <div className="tile__foot">
            {t(pieces.length === 1 ? 'tile.pieceInProgress' : 'tile.piecesInProgress')} {arrow}
          </div>
        </button>
      )
    }

    case 'quickNote':
      return (
        <div className="tile" style={{ flex: 1 }}>
          <Meta>{t('tile.quickNoteTitle')}</Meta>
          <textarea
            className="textarea"
            style={{
              background: 'transparent',
              padding: 0,
              minHeight: 52,
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--t-sm)',
            }}
            dir="auto"
            placeholder={t('tile.quickNotePlaceholder')}
            aria-label={t('tile.quickNoteTitle')}
            value={state.quickNote}
            onChange={(e) => setQuickNote(e.target.value)}
          />
          <div className="row" style={{ marginTop: 'auto' }}>
            <Button size="sm" onClick={pinNote} disabled={!state.quickNote.trim()}>
              {t('tile.pinIt')}
            </Button>
            <span className="grow" />
            <button className="btn btn--quiet btn--sm" onClick={() => go('library')}>
              {t('tile.notes')} {arrow}
            </button>
          </div>
        </div>
      )

    case 'weather':
      return <WeatherTile city={state.settings.city} />

    case 'player':
      return <PlayerTile go={go} />

    default:
      return null
  }
}

/* ------------------------------------------------------------------ weather */

const WEATHER_KEYS = [
  'weather.clear',
  'weather.partlyCloudy',
  'weather.overcast',
  'weather.rain',
  'weather.snow',
  'weather.showers',
  'weather.storms',
] as const

function weatherKey(code: number): (typeof WEATHER_KEYS)[number] {
  if (code === 0) return 'weather.clear'
  if (code <= 2) return 'weather.partlyCloudy'
  if (code <= 48) return 'weather.overcast'
  if (code <= 67) return 'weather.rain'
  if (code <= 77) return 'weather.snow'
  if (code <= 82) return 'weather.showers'
  return 'weather.storms'
}

function WeatherTile({ city }: { city: string }) {
  const t = useT()
  const [wx, setWx] = useState<{ temp: number; code: number } | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    // Geocode the configured city, then read its current conditions.
    // Open-Meteo needs no API key, which keeps the app deployable as a
    // pure static site.
    fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city)}&count=1`)
      .then((r) => r.json())
      .then((geo) => {
        const place = geo?.results?.[0]
        if (!place) throw new Error('unknown city')
        return fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${place.latitude}&longitude=${place.longitude}&current_weather=true`,
        )
      })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d?.current_weather) return
        setWx({
          temp: Math.round(d.current_weather.temperature),
          code: d.current_weather.weathercode,
        })
      })
      .catch(() => !cancelled && setFailed(true))
    return () => {
      cancelled = true
    }
  }, [city])

  return (
    <div className="tile tile--tinted" style={{ ...hueVars('amber'), flex: 1 }}>
      <Meta>{city}</Meta>
      {wx ? (
        <>
          <div className="tile__stat">{num(wx.temp)}°</div>
          <div className="tile__foot">{t(weatherKey(wx.code))}</div>
        </>
      ) : (
        <div className="tile__foot" style={{ marginTop: 'var(--s-3)' }}>
          {failed ? t('weather.unavailable') : t('weather.checking')}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------- player */

function PlayerTile({ go }: { go: (s: Screen) => void }) {
  const { current, playing, toggle, progress } = usePlayer()
  const t = useT()
  return (
    <div className="tile tile--tinted" style={{ ...hueVars(current?.hue ?? 'teal'), flex: 1 }}>
      <Meta>{t('tile.nowPlaying')}</Meta>
      <div className="row">
        <Button
          icon
          variant="primary"
          onClick={toggle}
          aria-label={t(playing ? 'player.pause' : 'player.play')}
        >
          {playing ? <IconPause /> : <IconPlay />}
        </Button>
        <div className="grow">
          <div className="truncate" style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>
            {current?.title ?? t('tile.nothingYet')}
          </div>
          <div className="truncate" style={{ fontSize: 'var(--t-xs)', color: 'var(--ink-2)' }}>
            {current?.artist ?? t('tile.pickSomething')}
          </div>
        </div>
      </div>
      <div style={{ marginTop: 'auto', paddingTop: 'var(--s-2)' }}>
        <Progress value={progress} hue={current?.hue} />
      </div>
      <button
        className="btn btn--quiet btn--sm"
        style={{ alignSelf: 'flex-start' }}
        onClick={() => go('library')}
      >
        {t('tile.library')} {t('common.arrow')}
      </button>
    </div>
  )
}
